import { useCallback, useEffect, useRef, useState } from 'react';

const CHANNEL_NAME = 'connect-widget-tab-sync';
const MASTER_LOCK_KEY = 'connect-widget-master-lock';
const CALL_STATE_KEY = 'connect-widget-call-active';
const AGENT_STATE_KEY = 'connect-widget-agent-state';
const HEARTBEAT_MS = 2000;
const STALE_MS = 7000;
// const MASTER_TAB_TITLE_PREFIX = '● ';
// const MASTER_TAB_TITLE_PREFIX = '🔴 ';
const MASTER_TAB_TITLE_PREFIX = '🔹';
const DEFAULT_BASE_TITLE = 'Adastra Connect';

const MSG = {
    FOCUS_MASTER: 'FOCUS_MASTER',
    CALL_STATE: 'CALL_STATE',
    AGENT_STATE: 'AGENT_STATE',
} as const;

export interface SyncedAgentState {
    isLoggedIn: boolean;
    agentStatus: string;
}

const DEFAULT_AGENT_STATE: SyncedAgentState = {
    isLoggedIn: false,
    agentStatus: 'Offline',
};

const CALL_STATUSES = ['Busy', 'CallingCustomer', 'PendingBusy', 'On call', 'Connected', 'Connecting'];

export function getConnectStatusColor(
    state: SyncedAgentState,
    callActive: boolean,
): string {
    if (!state.isLoggedIn) return '#A1A5B7';
    if (callActive || CALL_STATUSES.some((s) => state.agentStatus.toLowerCase().includes(s.toLowerCase()))) {
        return '#D7383C';
    }
    if (state.agentStatus === 'Available') return '#60BB1A';
    return '#A1A5B7';
}

export function getConnectStatusLabel(state: SyncedAgentState, callActive: boolean): string {
    if (!state.isLoggedIn) return 'Not signed in';
    if (callActive) return 'On call';
    return state.agentStatus || 'Offline';
}

interface MasterLock {
    tabId: string;
    lastHeartbeat: number;
}

function generateTabId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function readMasterLock(): MasterLock | null {
    try {
        const raw = localStorage.getItem(MASTER_LOCK_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as MasterLock;
    } catch {
        return null;
    }
}

function writeMasterLock(lock: MasterLock) {
    localStorage.setItem(MASTER_LOCK_KEY, JSON.stringify(lock));
}

function readSyncedCallActive(): boolean {
    try {
        const raw = localStorage.getItem(CALL_STATE_KEY);
        if (!raw) return false;
        return !!(JSON.parse(raw) as { active?: boolean }).active;
    } catch {
        return false;
    }
}

function writeSyncedCallActive(active: boolean) {
    localStorage.setItem(CALL_STATE_KEY, JSON.stringify({ active, ts: Date.now() }));
}

function readSyncedAgentState(): SyncedAgentState {
    try {
        const raw = localStorage.getItem(AGENT_STATE_KEY);
        if (!raw) return DEFAULT_AGENT_STATE;
        const parsed = JSON.parse(raw) as Partial<SyncedAgentState>;
        return {
            isLoggedIn: !!parsed.isLoggedIn,
            agentStatus: typeof parsed.agentStatus === 'string' ? parsed.agentStatus : 'Offline',
        };
    } catch {
        return DEFAULT_AGENT_STATE;
    }
}

function writeSyncedAgentState(state: SyncedAgentState) {
    localStorage.setItem(AGENT_STATE_KEY, JSON.stringify({ ...state, ts: Date.now() }));
}

export function useConnectTabElection() {
    const tabIdRef = useRef(generateTabId());
    const [isMasterTab, setIsMasterTab] = useState<boolean | null>(null);
    const [syncedCallActive, setSyncedCallActive] = useState(false);
    const [syncedAgentState, setSyncedAgentState] = useState<SyncedAgentState>(DEFAULT_AGENT_STATE);
    const channelRef = useRef<BroadcastChannel | null>(null);
    const baseTitleRef = useRef<string | null>(null);

    const getBaseTitle = useCallback((): string => {
        if (baseTitleRef.current) return baseTitleRef.current;
        const stripped = document.title.startsWith(MASTER_TAB_TITLE_PREFIX)
            ? document.title.slice(MASTER_TAB_TITLE_PREFIX.length)
            : document.title;
        baseTitleRef.current = stripped.trim() || DEFAULT_BASE_TITLE;
        return baseTitleRef.current;
    }, []);

    const tryClaimMaster = useCallback((): boolean => {
        const tabId = tabIdRef.current;
        const now = Date.now();
        const lock = readMasterLock();

        if (!lock || now - lock.lastHeartbeat > STALE_MS) {
            writeMasterLock({ tabId, lastHeartbeat: now });
        }

        return readMasterLock()?.tabId === tabId;
    }, []);

    useEffect(() => {
        const bc = new BroadcastChannel(CHANNEL_NAME);
        channelRef.current = bc;

        const evaluateRole = () => {
            setIsMasterTab(tryClaimMaster());
        };

        evaluateRole();
        setSyncedCallActive(readSyncedCallActive());
        setSyncedAgentState(readSyncedAgentState());

        bc.onmessage = (event: MessageEvent) => {
            const data = event.data;
            if (!data || typeof data !== 'object') return;

            if (data.type === MSG.CALL_STATE) {
                setSyncedCallActive(!!data.active);
            }

            if (data.type === MSG.AGENT_STATE && data.state) {
                setSyncedAgentState({
                    isLoggedIn: !!data.state.isLoggedIn,
                    agentStatus: typeof data.state.agentStatus === 'string' ? data.state.agentStatus : 'Offline',
                });
            }

            if (data.type === MSG.FOCUS_MASTER && tryClaimMaster()) {
                window.focus();
            }
        };

        const onStorage = (e: StorageEvent) => {
            if (e.key === MASTER_LOCK_KEY) {
                setIsMasterTab(tryClaimMaster());
            }
            if (e.key === CALL_STATE_KEY) {
                setSyncedCallActive(readSyncedCallActive());
            }
            if (e.key === AGENT_STATE_KEY) {
                setSyncedAgentState(readSyncedAgentState());
            }
        };

        window.addEventListener('storage', onStorage);

        const heartbeat = window.setInterval(() => {
            const tabId = tabIdRef.current;
            const lock = readMasterLock();
            if (lock?.tabId === tabId) {
                writeMasterLock({ tabId, lastHeartbeat: Date.now() });
            } else {
                setIsMasterTab(tryClaimMaster());
            }
        }, HEARTBEAT_MS);

        return () => {
            window.clearInterval(heartbeat);
            window.removeEventListener('storage', onStorage);
            bc.close();
            channelRef.current = null;

            const tabId = tabIdRef.current;
            const lock = readMasterLock();
            if (lock?.tabId === tabId) {
                localStorage.removeItem(MASTER_LOCK_KEY);
            }
        };
    }, [tryClaimMaster]);

    // Mark the browser tab so interpreters can spot the Connect master tab at a glance.
    useEffect(() => {
        const baseTitle = getBaseTitle();

        if (isMasterTab === true) {
            document.title = `${MASTER_TAB_TITLE_PREFIX}${baseTitle}`;
        } else if (isMasterTab === false) {
            document.title = baseTitle;
        }

        return () => {
            if (isMasterTab === true && document.title.startsWith(MASTER_TAB_TITLE_PREFIX)) {
                document.title = baseTitle;
            }
        };
    }, [isMasterTab, getBaseTitle]);

    const publishCallActive = useCallback((active: boolean) => {
        writeSyncedCallActive(active);
        channelRef.current?.postMessage({ type: MSG.CALL_STATE, active });
    }, []);

    const publishAgentState = useCallback((state: SyncedAgentState) => {
        writeSyncedAgentState(state);
        channelRef.current?.postMessage({ type: MSG.AGENT_STATE, state });
    }, []);

    const requestMasterFocus = useCallback(() => {
        channelRef.current?.postMessage({ type: MSG.FOCUS_MASTER });
    }, []);

    return {
        isMasterTab,
        syncedCallActive,
        syncedAgentState,
        publishCallActive,
        publishAgentState,
        requestMasterFocus,
    };
}
